import { formatDate } from "@/lib/utils";
import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import {
  DollarSign,
  Edit,
  Eye,
  MoreVertical,
  ChevronDown,
  ChevronUp,
  X,
  Loader2,
} from "lucide-react";
import apiClient from "../../api/apiClient";
import { API_ENDPOINTS } from "../../api/header";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import {
  FilterCard,
  FilterSearch,
  FilterActions,
} from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import { DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { PageContainer } from "@/components/layout/PageContainer";
import { Separator } from "@/components/ui/separator";

const Payments = () => {
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [formData, setFormData] = useState({
    id: null,
    payment_method: "",
    payment_status: "",
    payment_date: "",
    notes: "",
  });


  const permissions = useSelector((state) => state.userpermission_data);
  const currentStore = useSelector((state) => state.current_store);
  const storeId = currentStore?.id || null;
  const currency = currentStore?.default_currency;
  const currencySymbol = currency?.symbol || "";

  const hasPermission = React.useCallback(
    (permission) => {
      const perms = permissions || [];
      return perms && perms.includes(permission);
    },
    [permissions],
  );

  const [filters, setFilters] = useState({
    amount_min: "",
    amount_max: "",
    date_from: "",
    date_to: "",
    payment_method: "",
    payment_status: "",
    sale_id: "",
  });

  // Fetch payment methods available for sales scope
  const { data: availablePaymentMethods = [] } = useQuery({
    queryKey: ["payment-methods-available", "sales", storeId],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.PAYMENT_METHODS.AVAILABLE, {
        params: {
          scope: "sales",
          store_id: storeId || undefined,
        },
      });
      return res.data?.data ?? res.data ?? [];
    },
    enabled: hasPermission("payment-method read"),
    staleTime: 5 * 60 * 1000,
  });

  const { data: paymentsData, isLoading: loadingPayments } = useQuery({
    queryKey: ["payments", currentPage, pageSize, filters],
    queryFn: async () => {
      const queryParams = {
        page: currentPage,
        per_page: pageSize,
        with: "sale,sale.customer,sale.store,sale.user",
      };

      if (filters.amount_min) queryParams.amount_min = filters.amount_min;
      if (filters.amount_max) queryParams.amount_max = filters.amount_max;
      if (filters.date_from) queryParams.date_from = filters.date_from;
      if (filters.date_to) queryParams.date_to = filters.date_to;
      if (filters.payment_method && filters.payment_method !== "all")
        queryParams.payment_method = filters.payment_method;
      if (filters.payment_status && filters.payment_status !== "all")
        queryParams.payment_status = filters.payment_status;
      if (filters.sale_id) queryParams.sale_id = filters.sale_id;

      const res = await apiClient.get(API_ENDPOINTS.SALES.PAYMENTS, {
        params: queryParams,
      });
      return res.data;
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to fetch payments");
    },
  });

  const payments = React.useMemo(
    () => paymentsData?.data || [],
    [paymentsData?.data],
  );
  const paginationMeta = {
    current_page:
      paymentsData?.meta?.current_page || paymentsData?.current_page || 1,
    last_page: paymentsData?.meta?.last_page || paymentsData?.last_page || 1,
    per_page: paymentsData?.meta?.per_page || paymentsData?.per_page || 10,
    total: paymentsData?.meta?.total || paymentsData?.total || payments.length,
  };

  const updatePaymentMutation = useMutation({
    mutationFn: async (updateData) => {
      const url = API_ENDPOINTS.SALES.PAYMENT_DETAILS(formData.id);
      const res = await apiClient.put(url, updateData);
      return res.data;
    },
    onSuccess: () => {
      toast.success("Payment updated successfully");
      setModalOpen(false);
      queryClient.invalidateQueries(["payments"]);
      setFormData({
        id: null,
        payment_method: "",
        payment_status: "",
        payment_date: "",
        notes: "",
      });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update payment");
    },
  });

  const filteredData = useMemo(() => {
    return payments.filter((entry) =>
      Object.keys(entry).some((key) => {
        const value = entry[key];
        if (typeof value === "object" && value !== null) {
          return Object.values(value).some((v) =>
            String(v).toLowerCase().includes(searchText.toLowerCase()),
          );
        }
        return String(value).toLowerCase().includes(searchText.toLowerCase());
      }),
    );
  }, [payments, searchText]);

  const handleSave = async () => {
    const updateData = {
      payment_method: formData.payment_method,
      payment_status: formData.payment_status,
      payment_date: formData.payment_date,
      notes: formData.notes,
    };

    updatePaymentMutation.mutate(updateData);
  };

  const handleEdit = async (payment) => {
    setFormData({
      id: payment.id,
      payment_method: typeof payment.payment_method === 'object' && payment.payment_method !== null ? payment.payment_method.id : payment.payment_method,
      payment_status: payment.payment_status,
      payment_date: payment.payment_date,
      notes: payment.notes || "",
    });
    setModalOpen(true);
  };

  const handleView = (payment) => {
    setSelectedPayment(payment);
    setViewModalOpen(true);
  };

  const handleFilterApply = () => {
    setCurrentPage(1);
    queryClient.invalidateQueries(["payments"]);
  };

  const handleFilterReset = () => {
    setFilters({
      amount_min: "",
      amount_max: "",
      date_from: "",
      date_to: "",
      payment_method: "",
      payment_status: "",
      sale_id: "",
    });
    setSearchText("");
    setCurrentPage(1);
    setShowAdvancedFilters(false);
  };

  const getStatusBadgeVariant = (status) => {
    const statusMap = {
      completed: "success",
      pending: "warning",
      failed: "destructive",
      refunded: "secondary",
      cancelled: "destructive",
      "on-hold": "secondary",
      "partially-refunded": "secondary",
      "partially-paid": "warning",
    };
    return statusMap[status] || "secondary";
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "transaction_id",
        header: "Transaction ID",
        cell: ({ row }) => (
          <span className="font-medium text-primary">
            {row.original.transaction_id}
          </span>
        ),
      },
      {
        accessorKey: "sale.invoice_number",
        header: "Invoice No.",
        cell: ({ row }) => (
          <span className="font-medium">
            {row.original.sale?.invoice_number
              ? `#${row.original.sale?.invoice_number}`
              : "-"}
          </span>
        ),
      },
      {
        accessorKey: "amount",
        header: "Amount",
        cell: ({ row }) => (
          <span className="font-semibold text-green-600">
            {currencySymbol}
            {parseFloat(row.original.amount).toFixed(2)}
          </span>
        ),
      },
      {
        accessorKey: "payment_method",
        header: "Method",
        cell: ({ row }) => {
          const pm = row.original.payment_method;
          let pmName = "-";
          if (typeof pm === 'object' && pm !== null && pm.name) {
            pmName = pm.name;
          } else if (typeof pm === 'string') {
            pmName = pm.replace("_", " ").toUpperCase();
          }
          return (
            <Badge variant="outline" className="font-normal">
              {pmName}
            </Badge>
          );
        },
      },
      {
        accessorKey: "payment_status",
        header: "Status",
        cell: ({ row }) => {
          const status = row.original.payment_status;
          return status ? (
            <Badge
              variant={getStatusBadgeVariant(status)}
              className="capitalize"
            >
              {status?.replace(/-/g, " ")}
            </Badge>
          ) : (
            "-"
          );
        },
      },
      {
        accessorKey: "payment_date",
        header: "Payment Date",
        cell: ({ row }) => (
          <span className="text-sm">
            {formatDate(row.original.payment_date)}
          </span>
        ),
      },
      {
        accessorKey: "sale.customer",
        header: "Customer",
        cell: ({ row }) => {
          const customer = row.original.sale?.customer;
          return (
            <span className="text-sm">
              {customer?.name || customer?.full_name || (
                <span className="text-muted-foreground">Walk-In Customer</span>
              )}
            </span>
          );
        },
      },
      {
        accessorKey: "sale.store",
        header: "Store",
        cell: ({ row }) => (
          <span className="text-sm">
            {row.original.sale?.store?.name || "-"}
          </span>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleView(row.original)}>
                <Eye className="mr-2 h-4 w-4" /> View Details
              </DropdownMenuItem>
              {hasPermission("payment update") && (
                <DropdownMenuItem onClick={() => handleEdit(row.original)}>
                  <Edit className="mr-2 h-4 w-4" /> Edit
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [currencySymbol, hasPermission],
  );

  if (!hasPermission("payment read")) {
    return <Navigate to="/dashboard" />;
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Payments"
          subtitle="Manage Payment Transactions"
        />
      </PageHeader>

      <FilterCard>
        <FilterSearch
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setCurrentPage(1);
          }}
          placeholder="Search payments..."
        />
        <FilterActions>
          <Select
            value={filters.payment_method}
            onValueChange={(value) => {
              setFilters({ ...filters, payment_method: value });
              setCurrentPage(1);
            }}
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="All Methods" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Methods</SelectItem>
              {availablePaymentMethods.map((pm) => (
                <SelectItem key={pm.id || pm.slug || pm} value={pm.id || pm.slug || pm}>
                  {pm.name || pm}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.payment_status}
            onValueChange={(value) => {
              setFilters({ ...filters, payment_status: value });
              setCurrentPage(1);
            }}
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="refunded">Refunded</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="on-hold">On Hold</SelectItem>
              <SelectItem value="partially-refunded">
                Partially Refunded
              </SelectItem>
              <SelectItem value="partially-paid">Partially Paid</SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAdvancedFilters((v) => !v)}
            className="gap-1.5"
          >
            {showAdvancedFilters ? (
              <ChevronUp className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
            More
          </Button>

          {(filters.amount_min ||
            filters.amount_max ||
            filters.date_from ||
            filters.date_to ||
            filters.sale_id ||
            (filters.payment_method && filters.payment_method !== "all") ||
            (filters.payment_status && filters.payment_status !== "all")) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleFilterReset}
                className="gap-1.5 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
                Clear
              </Button>
            )}
        </FilterActions>

        {/* Advanced filters - inline expandable */}
        {showAdvancedFilters && (
          <>
            <Separator className="w-full" />
            <div className="w-full grid grid-cols-2 md:grid-cols-4 gap-3 pt-1">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">
                  Min Amount
                </Label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={filters.amount_min}
                  onChange={(e) =>
                    setFilters({ ...filters, amount_min: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">
                  Max Amount
                </Label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={filters.amount_max}
                  onChange={(e) =>
                    setFilters({ ...filters, amount_max: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">
                  Date From
                </Label>
                <Input
                  type="datetime-local"
                  value={filters.date_from}
                  onChange={(e) =>
                    setFilters({ ...filters, date_from: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Date To</Label>
                <Input
                  type="datetime-local"
                  value={filters.date_to}
                  onChange={(e) =>
                    setFilters({ ...filters, date_to: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Sale ID</Label>
                <Input
                  type="text"
                  placeholder="Enter sale ID"
                  value={filters.sale_id}
                  onChange={(e) =>
                    setFilters({ ...filters, sale_id: e.target.value })
                  }
                />
              </div>
              <div className="flex items-end">
                <Button
                  size="sm"
                  onClick={handleFilterApply}
                  className="w-full md:w-auto"
                >
                  Apply Filters
                </Button>
              </div>
            </div>
          </>
        )}
      </FilterCard>

      <TableCard>
        <DataTable
          columns={columns}
          data={filteredData}
          pagination={{
            pageIndex: currentPage - 1,
            pageSize: pageSize,
          }}
          pageCount={paginationMeta.last_page}
          onPaginationChange={(updater) => {
            if (typeof updater === "function") {
              const newState = updater({
                pageIndex: currentPage - 1,
                pageSize,
              });
              setCurrentPage(newState.pageIndex + 1);
              setPageSize(newState.pageSize);
            }
          }}
          isLoading={loadingPayments}
          enableRowSelection={true}
          footerInfo={
            <div className="flex items-center gap-4">
              <span className="font-medium text-foreground">
                Total Payments: {paginationMeta.total || 0}
              </span>
            </div>
          }
        />
      </TableCard>

      {/* View Payment Modal */}
      <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto px-1">
          <DialogHeader className="px-5 pt-5">
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Payment Details
              {selectedPayment?.transaction_id
                ? ` - ${selectedPayment.transaction_id}`
                : ""}
            </DialogTitle>
            <DialogDescription>
              Full details for this payment transaction including sale and
              customer information.
            </DialogDescription>
          </DialogHeader>

          {selectedPayment && (
            <div className="space-y-4 px-5 pb-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Transaction Info */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">
                      Transaction Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Transaction ID:
                      </span>
                      <span className="font-mono font-medium">
                        {selectedPayment.transaction_id || "-"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Amount:</span>
                      <span className="font-bold text-emerald-600">
                        {currencySymbol}
                        {parseFloat(selectedPayment.amount).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Status:</span>
                      <Badge
                        variant={getStatusBadgeVariant(
                          selectedPayment.payment_status,
                        )}
                        className="capitalize"
                      >
                        {selectedPayment.payment_status?.replace(/-/g, " ")}
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Method:</span>
                      <Badge variant="outline" className="capitalize">
                        {typeof selectedPayment.payment_method === 'object' && selectedPayment.payment_method !== null
                          ? selectedPayment.payment_method.name
                          : selectedPayment.payment_method
                            ?.replace("_", " ")
                            .toUpperCase()}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Date:</span>
                      <span className="font-medium">
                        {formatDate(selectedPayment.payment_date,)}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                {/* Sale & Customer */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Sale & Customer</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Invoice #:</span>
                      <span className="font-medium text-primary">
                        {selectedPayment.sale?.invoice_number
                          ? `#${selectedPayment.sale.invoice_number}`
                          : "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Customer:</span>
                      <span className="font-medium">
                        {selectedPayment.sale?.customer?.name ||
                          selectedPayment.sale?.customer?.full_name ||
                          "Walk-In Customer"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Store:</span>
                      <span className="font-medium">
                        {selectedPayment.sale?.store?.name || "N/A"}
                      </span>
                    </div>
                    {selectedPayment.card_type && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Card:</span>
                        <span className="font-medium capitalize">
                          {selectedPayment.card_type}
                          {selectedPayment.card_last_four
                            ? ` •••• ${selectedPayment.card_last_four}`
                            : ""}
                        </span>
                      </div>
                    )}
                    {selectedPayment.authorization_code && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Auth Code:
                        </span>
                        <span className="font-medium font-mono">
                          {selectedPayment.authorization_code}
                        </span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Notes */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground italic">
                    {selectedPayment.notes ||
                      "No notes provided for this payment."}
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          <DialogFooter className="px-5 pb-5">
            <Button variant="outline" onClick={() => setViewModalOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Payment Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Edit Payment
            </DialogTitle>
            <DialogDescription>
              Update payment method, status, or date.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>
                Payment Method <span className="text-destructive">*</span>
              </Label>
              <Select
                value={formData.payment_method}
                onValueChange={(value) =>
                  setFormData({ ...formData, payment_method: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Method" />
                </SelectTrigger>
                <SelectContent>
                  {availablePaymentMethods.map((pm) => (
                    <SelectItem key={pm.id || pm.slug || pm} value={pm.id || pm.slug || pm}>
                      {pm.name || pm}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>
                Payment Status <span className="text-destructive">*</span>
              </Label>
              <Select
                value={formData.payment_status}
                onValueChange={(value) =>
                  setFormData({ ...formData, payment_status: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="refunded">Refunded</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="on-hold">On Hold</SelectItem>
                  <SelectItem value="partially-refunded">
                    Partially Refunded
                  </SelectItem>
                  <SelectItem value="partially-paid">Partially Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>
                Payment Date <span className="text-destructive">*</span>
              </Label>
              <Input
                type="datetime-local"
                value={
                  formData.payment_date
                    ? formData.payment_date.slice(0, 16)
                    : ""
                }
                onChange={(e) =>
                  setFormData({ ...formData, payment_date: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Enter payment notes"
                rows={3}
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setModalOpen(false)}
              disabled={updatePaymentMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={updatePaymentMutation.isPending}
            >
              {updatePaymentMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Update Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};

export default Payments;
